import { Component, EventEmitter, Input, OnInit, Output, signal, computed } from "@angular/core";

import { FormsModule } from "@angular/forms";
import { ArticleTemplateService } from "../../../_services";
import { ArticleTemplate } from "../../../_models";

/**
 * Einfaches Interface für Projekt-Referenz
 */
export interface PickerProjectRef {
  id: number;
  name: string;
}

/**
 * Einfaches Interface für Kunden-Referenz
 */
export interface PickerCustomerRef {
  id: number;
  name: string;
}

@Component({
  selector: "app-article-template-picker",
  standalone: true,
  imports: [FormsModule],
  templateUrl: "./article-template-picker.component.html",
})
export class ArticleTemplatePickerComponent implements OnInit {
  /** Projekt für Filter (optional) */
  @Input() project: PickerProjectRef | null = null;

  /** Kunde für Filter (optional) */
  @Input() customer: PickerCustomerRef | null = null;

  /** Initiale Suchanfrage */
  @Input() initialSearchQuery = "";

  /**
   * Optionale externe Artikelvorlagen-Liste.
   * Wenn gesetzt, werden die Templates nicht selbst geladen.
   */
  @Input() externalTemplates: ArticleTemplate[] | null = null;

  /** Emittiert die ausgewählte Artikelvorlage */
  @Output() templateSelected = new EventEmitter<ArticleTemplate>();

  /** Emittiert wenn Modal geschlossen wird */
  @Output() closed = new EventEmitter<void>();

  /**
   * Emittiert die neu geladenen Templates nach einem Reload.
   * Kann verwendet werden um die externe Liste zu aktualisieren.
   */
  @Output() templatesReloaded = new EventEmitter<ArticleTemplate[]>();

  // Daten
  articleTemplates: ArticleTemplate[] = [];
  isLoading = false;

  // Filter
  searchQuery = signal<string>("");
  filterByProject = signal<boolean>(false);
  filterByCustomer = signal<boolean>(false);

  // Paginierung
  displayLimit = signal<number>(30);
  readonly PAGE_SIZE = 30;

  // Gefilterte Templates
  filteredTemplates = computed(() => {
    let filtered = [...this.articleTemplates];

    // Textsuche
    const query = this.searchQuery().toLowerCase().trim();
    if (query) {
      filtered = filtered.filter(
        (t) =>
          t.description?.toLowerCase().includes(query) ||
          t.part_nr?.toLowerCase().includes(query) ||
          t.vendor?.toLowerCase().includes(query),
      );
    }

    // Projekt-Filter
    if (this.filterByProject() && this.project?.id) {
      filtered = filtered.filter((t) => {
        if (!t.comment2) return false;
        const projectIds = t.comment2.split(",").map((id) => id.trim());
        return projectIds.includes(String(this.project!.id));
      });
    }

    // Kunden-Filter
    if (this.filterByCustomer() && this.customer?.id) {
      filtered = filtered.filter((t) => {
        if (!t.comment3) return false;
        return t.comment3.trim() === String(this.customer!.id);
      });
    }

    return filtered;
  });

  // Sichtbare Templates (mit Paginierung)
  visibleTemplates = computed(() => {
    return this.filteredTemplates().slice(0, this.displayLimit());
  });

  hasMoreTemplates = computed(() => {
    return this.filteredTemplates().length > this.displayLimit();
  });

  remainingCount = computed(() => {
    return this.filteredTemplates().length - this.displayLimit();
  });

  constructor(private articleTemplateService: ArticleTemplateService) {}

  ngOnInit(): void {
    this.searchQuery.set(this.initialSearchQuery);
    // Kundenfilter automatisch aktivieren wenn Kunde gesetzt
    this.filterByCustomer.set(!!this.customer?.id);

    // Prüfen ob externe Templates übergeben wurden
    if (this.externalTemplates && this.externalTemplates.length > 0) {
      this.articleTemplates = this.externalTemplates;
      this.isLoading = false;
    } else {
      this.loadTemplates();
    }
  }

  private loadTemplates(): void {
    this.isLoading = true;
    this.articleTemplateService.all().subscribe({
      next: (data) => {
        this.articleTemplates = data;
        this.isLoading = false;
      },
      error: (err) => {
        console.error("Fehler beim Laden der Artikelvorlagen:", err);
        this.isLoading = false;
      },
    });
  }

  /**
   * Lädt die Artikelvorlagen neu und emittiert die neuen Templates
   */
  reloadTemplates(): void {
    this.isLoading = true;
    this.articleTemplateService.all().subscribe({
      next: (data) => {
        this.articleTemplates = data;
        this.isLoading = false;
        // Event emittieren damit externe Liste aktualisiert werden kann
        this.templatesReloaded.emit(data);
      },
      error: (err) => {
        console.error("Fehler beim Laden der Artikelvorlagen:", err);
        this.isLoading = false;
      },
    });
  }

  /**
   * Wird aufgerufen wenn sich die Suche ändert
   */
  onSearchChange(value: string): void {
    this.searchQuery.set(value);
    this.displayLimit.set(this.PAGE_SIZE);
  }

  /**
   * Toggle Projekt-Filter
   */
  toggleProjectFilter(): void {
    this.filterByProject.update((v) => !v);
    this.displayLimit.set(this.PAGE_SIZE);
  }

  /**
   * Toggle Kunden-Filter
   */
  toggleCustomerFilter(): void {
    this.filterByCustomer.update((v) => !v);
    this.displayLimit.set(this.PAGE_SIZE);
  }

  /**
   * Zeigt mehr Templates an
   */
  showMore(): void {
    this.displayLimit.update((l) => l + this.PAGE_SIZE);
  }

  /**
   * Wählt eine Vorlage aus
   */
  selectTemplate(template: ArticleTemplate): void {
    this.templateSelected.emit(template);
  }

  /**
   * Schließt das Modal
   */
  close(): void {
    this.closed.emit();
  }
}
